import React, { useState, useEffect, useRef } from 'react';
import { Search, X, MapPin, Calendar, BookOpen, Route as RouteIcon, Bike, Footprints, Car } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './GlobalSearch.css';

const GlobalSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const searchRef = useRef(null);
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Debounced search
  useEffect(() => {
    if (searchTerm.length < 2) {
      setResults(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedCategory]);

  const performSearch = async (query) => {
    if (query.length < 2) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${BACKEND_URL}/api/search?q=${encodeURIComponent(query)}&category=${selectedCategory}`,
        { headers }
      );

      if (response.ok) {
        const data = await response.json();
        setResults(data);
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result) => {
    if (result.type === 'route') {
      navigate(`/routes/${result.id}`);
    } else if (result.type === 'poi') {
      navigate(`/pois?highlight=${result.id}`);
    } else if (result.type === 'event') {
      navigate(`/events?highlight=${result.id}`);
    } else if (result.type === 'knowledge') {
      navigate(result.url);
    }
    
    setSearchTerm('');
    setResults(null);
    setIsOpen(false);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setResults(null);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getResultIcon = (type) => {
    switch (type) {
      case 'route':
        return <RouteIcon size={20} />;
      case 'poi':
        return <MapPin size={20} />;
      case 'event':
        return <Calendar size={20} />;
      case 'knowledge':
        return <BookOpen size={20} />;
      default:
        return <Search size={20} />;
    }
  };

  const getCategoryLabel = (type) => {
    const labels = {
      route: 'Route',
      poi: 'POI',
      event: 'Event',
      knowledge: 'Wissen'
    };
    return labels[type] || type;
  };

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'cycling':
        return <Bike size={14} />;
      case 'hiking':
        return <Footprints size={14} />;
      case 'roadtrip':
        return <Car size={14} />;
      default:
        return null;
    }
  };

  const getActivityLabel = (activityType) => {
    const labels = {
      cycling: 'Radfahren',
      hiking: 'Wandern',
      roadtrip: 'Roadtrip'
    };
    return labels[activityType] || activityType;
  };

  return (
    <div className="global-search-container" ref={searchRef}>
      <div className="search-input-wrapper">
        <Search className="search-icon" size={20} />
        <input
          type="text"
          className="search-input"
          placeholder="Suche nach Routen, POIs, Events..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => searchTerm.length >= 2 && setIsOpen(true)}
        />
        {searchTerm && (
          <button className="clear-search-btn" onClick={clearSearch}>
            <X size={18} />
          </button>
        )}
      </div>

      {isOpen && results && (
        <div className="search-results-dropdown">
          <div className="search-filters">
            <button
              className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              Alle ({results.total})
            </button>
            <button
              className={`filter-btn ${selectedCategory === 'routes' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('routes')}
            >
              Routen ({results.routes.length})
            </button>
            <button
              className={`filter-btn ${selectedCategory === 'pois' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('pois')}
            >
              POIs ({results.pois.length})
            </button>
            <button
              className={`filter-btn ${selectedCategory === 'events' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('events')}
            >
              Events ({results.events.length})
            </button>
            <button
              className={`filter-btn ${selectedCategory === 'knowledge' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('knowledge')}
            >
              Wissen ({results.knowledge.length})
            </button>
          </div>

          <div className="search-results-list">
            {loading ? (
              <div className="search-loading">Suche läuft...</div>
            ) : results.total === 0 ? (
              <div className="search-empty">
                <p>Keine Ergebnisse für "{searchTerm}"</p>
              </div>
            ) : (
              <>
                {results.routes.length > 0 && (
                  <div className="result-category">
                    <h4>Routen</h4>
                    {results.routes.map((route) => (
                      <div
                        key={route.id}
                        className="search-result-item"
                        onClick={() => handleResultClick(route)}
                      >
                        <div className="result-icon">{getResultIcon('route')}</div>
                        <div className="result-content">
                          <h5>{route.name}</h5>
                          <p>{route.description}</p>
                          <div className="result-meta">
                            {route.activity_type && (
                              <span className={`activity-badge activity-${route.activity_type}`}>
                                {getActivityIcon(route.activity_type)}
                                {getActivityLabel(route.activity_type)}
                              </span>
                            )}
                            <span>{route.region}</span>
                            <span>•</span>
                            <span>{route.distance_km} km</span>
                            <span>•</span>
                            <span>{route.difficulty}</span>
                          </div>
                        </div>
                        <span className="result-badge">{getCategoryLabel('route')}</span>
                      </div>
                    ))}
                  </div>
                )}

                {results.pois.length > 0 && (
                  <div className="result-category">
                    <h4>Points of Interest</h4>
                    {results.pois.map((poi) => (
                      <div
                        key={poi.id}
                        className="search-result-item"
                        onClick={() => handleResultClick(poi)}
                      >
                        <div className="result-icon">{getResultIcon('poi')}</div>
                        <div className="result-content">
                          <h5>{poi.name}</h5>
                          <p>{poi.description}</p>
                          <div className="result-meta">
                            <span>{poi.subcategory}</span>
                            {poi.activity_types && poi.activity_types.length > 0 && (
                              <>
                                <span>•</span>
                                <div className="activity-badges-inline">
                                  {poi.activity_types.map((activity) => (
                                    <span key={activity} className={`activity-badge activity-${activity}`}>
                                      {getActivityIcon(activity)}
                                      {getActivityLabel(activity)}
                                    </span>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <span className="result-badge">{getCategoryLabel('poi')}</span>
                      </div>
                    ))}
                  </div>
                )}

                {results.events.length > 0 && (
                  <div className="result-category">
                    <h4>Events</h4>
                    {results.events.map((event) => (
                      <div
                        key={event.id}
                        className="search-result-item"
                        onClick={() => handleResultClick(event)}
                      >
                        <div className="result-icon">{getResultIcon('event')}</div>
                        <div className="result-content">
                          <h5>{event.title}</h5>
                          <p>{event.description}</p>
                          <div className="result-meta">
                            <span>{event.venue_name}</span>
                            <span>•</span>
                            <span>{event.date_from}</span>
                          </div>
                        </div>
                        <span className="result-badge">{getCategoryLabel('event')}</span>
                      </div>
                    ))}
                  </div>
                )}

                {results.knowledge.length > 0 && (
                  <div className="result-category">
                    <h4>Wissen</h4>
                    {results.knowledge.map((article) => (
                      <div
                        key={article.id}
                        className="search-result-item"
                        onClick={() => handleResultClick(article)}
                      >
                        <div className="result-icon">{getResultIcon('knowledge')}</div>
                        <div className="result-content">
                          <h5>{article.title}</h5>
                          <p>{article.description}</p>
                        </div>
                        <span className="result-badge">{getCategoryLabel('knowledge')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
